import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Button, Card, CardBody, Col, Collapse, Row} from 'reactstrap';
import {AvForm} from 'availity-reactstrap-validation';
import {IRootState} from 'app/shared/reducers';
import {setRegistrationData} from 'app/entities/registration/registration.reducer';
import {
  getStartCouncilSuggestions,
  getStartFacilitySuggestions,
  getStartVillageSuggestions,
} from 'app/entities/healthcare-facility/healthcare-facility.reducer';
import 'app/entities/registration/registration.scss';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {IHealthcareFacility} from 'app/shared/model/healthcare-facility.model';
import {IRoute} from 'app/shared/model/route.model';
import {IMMamaSettings} from 'app/shared/model/mmama-settings-model';
import {convertSecondsToMinutes} from 'app/shared/util/date-utils';
import RegistrationButtons from './registration-buttons';

interface FacilitySuggestion {
  facility: IHealthcareFacility;
  distance: number;
  durationSeconds: number;
  routeCouncil?: IRoute;
  route?: IRoute;
}

interface IFacilitySuggestionsProps extends StateProps, DispatchProps {
  handlePreviousStep: () => void;
  handleNextStep: () => void;
  currentStep: number;
  onFinish?: () => void;
  updating?: boolean;
  countrySettings: IMMamaSettings | null;
}

const FacilitySuggestions = (props: IFacilitySuggestionsProps) => {
  const {
    registrationData,
    startCouncilSuggestions,
    startFacilitySuggestions,
    startVillageSuggestions,
    hfSuggestionsLoading,
    countrySettings,
    onFinish,
    updating
  } = props;

  const [facilitySuggestions, setFacilitySuggestions] = useState<FacilitySuggestion[]>([]);
  const [openLevels, setOpenLevels] = useState<{[key: number]: boolean}>({});

  // Fetch facility suggestions when component mounts
  useEffect(() => {
    if (countrySettings?.id) {
      let requiredLevel = 1; // Default to level 1 for non-pregnancy cases
      if (registrationData.healthcareFacility && typeof registrationData.healthcareFacility.level === 'number') {
        requiredLevel = countrySettings?.enableSameFacilityLevel
          ? registrationData.healthcareFacility.level
          : registrationData.healthcareFacility.level + 1;
      }

      if (countrySettings.useGeofence) {
        // Use council-based suggestions when geofence is enabled
        if (registrationData.village?.council?.id) {
          props.getStartCouncilSuggestions(registrationData.village.council.id, requiredLevel);
        } else if (registrationData.healthcareFacility?.council?.id) {
          props.getStartCouncilSuggestions(registrationData.healthcareFacility.council.id, requiredLevel);
        }
      } else {
        // Use village/facility-based suggestions when geofence is disabled
        if (registrationData.villageId) {
          props.getStartVillageSuggestions(registrationData.villageId, requiredLevel);
        } else if (registrationData.healthcareFacilityId) {
          props.getStartFacilitySuggestions(registrationData.healthcareFacilityId, requiredLevel);
        }
      }
    }
  }, [countrySettings?.id, countrySettings?.useGeofence, countrySettings?.enableSameFacilityLevel, registrationData.village, registrationData.healthcareFacility, registrationData.villageId, registrationData.healthcareFacilityId]);

  // Format suggestions
  useEffect(() => {
    if (startFacilitySuggestions && startFacilitySuggestions.length > 0) {
      const formattedSuggestions = startFacilitySuggestions
        .filter(suggestion => !registrationData.healthcareFacilityId || suggestion.healthcareFacility?.id !== registrationData.healthcareFacilityId)
        .map(suggestion => ({
          facility: suggestion.healthcareFacility,
          distance: suggestion.route?.distance,
          durationSeconds: suggestion.route?.durationSeconds,
          routeCouncil: null,
          route: suggestion.route
        }));
      setFacilitySuggestions(formattedSuggestions);
    } else if (startVillageSuggestions && startVillageSuggestions.length > 0) {
      const formattedSuggestions = startVillageSuggestions.map(suggestion => ({
        facility: suggestion.healthcareFacility,
        distance: suggestion.route?.distance,
        durationSeconds: suggestion.route?.durationSeconds,
        routeCouncil: null,
        route: suggestion.route
      }));
      setFacilitySuggestions(formattedSuggestions);
    } else if (startCouncilSuggestions && startCouncilSuggestions.length > 0) {
      const formattedSuggestions = startCouncilSuggestions
        .filter(suggestion => !registrationData.healthcareFacilityId || suggestion.healthcareFacility?.id !== registrationData.healthcareFacilityId)
        .map(suggestion => ({
          facility: suggestion.healthcareFacility,
          distance: suggestion.routeCouncil?.distance,
          durationSeconds: suggestion.routeCouncil?.durationSeconds,
          routeCouncil: suggestion.routeCouncil,
          route: suggestion.route
        }));
      setFacilitySuggestions(formattedSuggestions);
    }
  }, [startCouncilSuggestions, startFacilitySuggestions, startVillageSuggestions, registrationData.healthcareFacilityId]);

  const toggleCollapseLevel = (level: number) => {
    setOpenLevels(prev => ({
      ...prev,
      [level]: !prev[level]
    }));
  };

  const groupFacilitiesByLevel = (suggestions: FacilitySuggestion[]) => {
    const maxPerLevel = countrySettings?.geofenceHfLimit || suggestions.length;

    return suggestions.reduce((acc, suggestion) => {
      const level = suggestion.facility.level || 1;
      if (!acc[level]) {
        acc[level] = [];
      }
      // Only add if we haven't reached the limit for this level
      if (acc[level].length < maxPerLevel) {
        acc[level].push(suggestion);
      }
      return acc;
    }, {} as {[key: number]: FacilitySuggestion[]});
  };

  // Health Facility Suggestion component
  const HealthFacilitySuggestion = ({facility, distance, durationSeconds}) => {
    return (
      <Card className="mb-3 shadow-sm">
        <CardBody>
          <Row>
            <Col xs="12">
              <h5 className="mb-1">{facility.name}</h5>
              <p className="text-muted mb-2">
                {facility.council?.name && `${facility.council.name}, `}
                {facility.council?.parent?.name && `${facility.council.parent.name}: `}
                Level {facility.level}, {facility.hfCategory}
              </p>
            </Col>

            {facility.description && (
              <Col xs="12" className="mb-2">
                <p className="mb-0 text-muted">{facility.description}</p>
              </Col>
            )}

            <Col xs="12">
              <Row>
                <Col xs="6" md="4">
                  <FontAwesomeIcon icon="map-marker-alt" className="text-primary mr-1"/>
                  <span className="text-muted">Route</span>
                  <div className="font-weight-bold">~{distance?.toFixed(2)}km</div>
                </Col>
                <Col xs="6" md="4">
                  <FontAwesomeIcon icon="clock" className="text-primary mr-1"/>
                  <span className="text-muted">Est. Time</span>
                  <div className="font-weight-bold">
                    {durationSeconds ? `~${convertSecondsToMinutes(durationSeconds).toFixed(1)} min` : 'N/A'}
                  </div>
                </Col>
                {facility.phoneNumber && (
                  <Col xs="12" md="4">
                    <FontAwesomeIcon icon="phone-alt" className="text-primary mr-1"/>
                    <span className="text-muted">Phone</span>
                    <div className="font-weight-bold">{facility.phoneNumber}</div>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </CardBody>
      </Card>
    );
  };

  return (
    <div>
      <h2 className="mb-4">This isn&apos;t a pregnancy-related case</h2>
      <p className="mb-4">Please direct the caller to:</p>

      <AvForm onValidSubmit={props.onFinish}>
        {hfSuggestionsLoading && (
          <div className="text-center py-4">
            <FontAwesomeIcon icon="spinner" spin size="2x"/> <p className="mt-2">Loading facility suggestions...</p>
          </div>
        )}

      {!hfSuggestionsLoading && facilitySuggestions && facilitySuggestions.length > 0 && (
        countrySettings?.useGeofence ? (
          // Grouped by level when geofence is enabled
          (() => {
            const groupedFacilities = groupFacilitiesByLevel(facilitySuggestions);
            const sortedLevels = Object.keys(groupedFacilities).map(Number).sort((a, b) => a - b);

            return sortedLevels.map(level => (
              <div key={level} className="mb-3">
                <div
                  className="d-flex align-items-center cursor-pointer p-2 bg-light rounded"
                  onClick={() => toggleCollapseLevel(level)}
                  style={{ cursor: 'pointer' }}
                >
                  <FontAwesomeIcon
                    icon={openLevels[level] ? "chevron-down" : "chevron-right"}
                    className="mr-2"
                  />
                  <h6 className="mb-0 font-weight-bold">HF Level {level}</h6>
                </div>
                <Collapse isOpen={openLevels[level]}>
                  <div className="mt-2">
                    {groupedFacilities[level].map((facilitySuggestion: FacilitySuggestion) => (
                      <HealthFacilitySuggestion
                        key={facilitySuggestion.facility.id}
                        facility={facilitySuggestion.facility}
                        distance={facilitySuggestion.distance}
                        durationSeconds={facilitySuggestion.durationSeconds}
                      />
                    ))}
                  </div>
                </Collapse>
              </div>
            ));
          })()
        ) : (
          // Regular list when geofence is disabled
          facilitySuggestions.map((facilitySuggestion: FacilitySuggestion) => (
            <HealthFacilitySuggestion
              key={facilitySuggestion.facility.id}
              facility={facilitySuggestion.facility}
              distance={facilitySuggestion.distance}
              durationSeconds={facilitySuggestion.durationSeconds}
            />
          ))
        )
      )}

        {!hfSuggestionsLoading && facilitySuggestions.length === 0 && (
          <div className="text-center py-4 text-muted">
            <p>No health facilities available. Please try a different selection.</p>
          </div>
        )}

        <RegistrationButtons
          currentStep={props.currentStep}
          handlePreviousStep={props.handlePreviousStep}
          isLastStep={true}
          updating={updating}
        />
      </AvForm>
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  registrationData: state.registration.entity,
  startCouncilSuggestions: state.healthcareFacility.startCouncilSuggestions,
  startFacilitySuggestions: state.healthcareFacility.startFacilitySuggestions,
  startVillageSuggestions: state.healthcareFacility.startVillageSuggestions,
  hfSuggestionsLoading: state.healthcareFacility.loading,
});

const mapDispatchToProps = {
  setRegistrationData,
  getStartCouncilSuggestions,
  getStartFacilitySuggestions,
  getStartVillageSuggestions,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(FacilitySuggestions);
